import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/activity.scss';
import { useActivity, vehicleFilters, driverStateFilters } from '../hooks/useActivity';
import { FiRefreshCw } from 'react-icons/fi';

const normalizePhone = (val) => (val ? String(val).replace(/\D/g, '') : '');

const isTripStillActive = (tripState) => {
    const normalized = String(tripState || '').toLowerCase();
    if (!normalized) return true;
    return !['completed', 'cancelled', 'faild'].includes(normalized);
};

const getAssignedDriverId = (request) => {
    if (!request) return null;
    if (request.acceptedDriverId) return String(request.acceptedDriverId);
    if (request.tripDriverId) return String(request.tripDriverId);
    const acceptedOffer = (request.offers || []).find((offer) => String(offer?.status || '').toLowerCase() === 'accepted');
    if (acceptedOffer?.driverId) return String(acceptedOffer.driverId);
    if (acceptedOffer?.driver?._id) return String(acceptedOffer.driver._id);
    return null;
};

const driverDotIcon = L.divIcon({
    className: 'driver-dot',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
});

const driverNotifiedDotIcon = L.divIcon({
    className: 'driver-notified-dot',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
});

const driverOfferDotIcon = L.divIcon({
    className: 'driver-offer-dot',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
});

const requestDotIcon = L.divIcon({
    className: 'request-dot',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
});

const destinationPinIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const Recenter = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center);
    }, [center, map]);
    return null;
};

export const Activity = () => {
    const user = useSelector((state) => state.userController.user);
    const navigate = useNavigate();
    const {
        drivers,
        requests,
        loading,
        filter,
        driverStateFilter,
        applyFilter,
        applyDriverStateFilter,
        refresh,
        selectRequest,
        selectedRequestId,
        maxSearchRangeKm,
        redirectRequest,
        notifyDriverIds,
        updateNotifyList,
    } = useActivity();
    const mapRef = useRef(null);
    const [selectedDriverId, setSelectedDriverId] = useState(null);
    const [redirectDriverId, setRedirectDriverId] = useState('');
    const [redirecting, setRedirecting] = useState(false);
    const [redirectSearchInput, setRedirectSearchInput] = useState('');
    const [redirectSearchResults, setRedirectSearchResults] = useState([]);
    const [redirectSearchTerm, setRedirectSearchTerm] = useState('');
    const [redirectSearchLoading, setRedirectSearchLoading] = useState(false);
    const [notifySelection, setNotifySelection] = useState([]);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [savingNotify, setSavingNotify] = useState(false);
    const [notifySearchInput, setNotifySearchInput] = useState('');
    const [notifySearchTerm, setNotifySearchTerm] = useState('');
    const [mapPhoneFilter, setMapPhoneFilter] = useState('');

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        const normalized = Array.isArray(notifyDriverIds) ? notifyDriverIds.map((id) => String(id)) : [];
        setNotifySelection(normalized);
    }, [notifyDriverIds, showNotifyModal]);

    useEffect(() => {
        if (showNotifyModal) {
            setNotifySearchInput('');
            setNotifySearchTerm('');
        }
    }, [showNotifyModal]);

    const center = useMemo(() => {
        const points = [];
        drivers.forEach((driver) => {
            if (driver?.lastLocation?.latitude && driver?.lastLocation?.longitude) {
                points.push({
                    lat: driver.lastLocation.latitude,
                    lng: driver.lastLocation.longitude,
                });
            }
        });
        requests.forEach((req) => {
            if (req?.startPoint?.latitude && req?.startPoint?.longitude) {
                points.push({ lat: req.startPoint.latitude, lng: req.startPoint.longitude });
            }
        });
        if (!points.length) {
            return [32.8872, 13.1913];
        }
        const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
        const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
        return [avgLat, avgLng];
    }, [drivers, requests]);

    const driverLookup = useMemo(() => {
        const map = new Map();
        drivers.forEach((driver) => {
            map.set(String(driver._id), driver);
        });
        return map;
    }, [drivers]);

    const selectedNotifyDrivers = useMemo(
        () => notifySelection.map((id) => driverLookup.get(String(id))).filter(Boolean),
        [notifySelection, driverLookup]
    );

    const filteredDrivers = useMemo(() => {
        const term = normalizePhone(notifySearchTerm);
        if (!term) return [];
        return drivers.filter((driver) => normalizePhone(driver.phoneNumber).includes(term));
    }, [drivers, notifySearchTerm]);

    const handleNotifySearch = () => {
        setNotifySearchTerm(notifySearchInput);
    };

    const selectedRequest = useMemo(
        () => requests.find((r) => String(r._id) === String(selectedRequestId)) || null,
        [requests, selectedRequestId]
    );

    const selectedAssignedDriverId = useMemo(
        () => getAssignedDriverId(selectedRequest),
        [selectedRequest]
    );

    const shouldIsolateAssignedDriver = useMemo(() => {
        if (!selectedRequest || selectedRequest.status !== 'accepted') return false;
        return isTripStillActive(selectedRequest.tripState);
    }, [selectedRequest]);

    useEffect(() => {
        setRedirectDriverId('');
    }, [selectedRequestId]);

    const offerDrivers = useMemo(() => {
        const ids = new Set();
        const phones = new Set();
        if (!selectedRequest?.offers) return { ids, phones };
        selectedRequest.offers.forEach((offer) => {
            if (offer.driverId) ids.add(String(offer.driverId));
            if (offer.driver?._id) ids.add(String(offer.driver._id));
            const phone = normalizePhone(offer.driverPhoneNumber || offer.driver?.phoneNumber);
            if (phone) phones.add(phone);
        });
        return { ids, phones };
    }, [selectedRequest]);

    const notifiedDriverSet = useMemo(
        () => new Set((selectedRequest?.notifiedDriverIds || []).map((id) => String(id))),
        [selectedRequest]
    );

    const mapDrivers = useMemo(() => {
        const withLocation = drivers.filter((driver) => driver?.lastLocation?.latitude && driver?.lastLocation?.longitude);
        if (!shouldIsolateAssignedDriver || !selectedAssignedDriverId) {
            return withLocation;
        }

        const assigned = withLocation.find((driver) => String(driver._id) === String(selectedAssignedDriverId));
        if (assigned) {
            return [assigned];
        }

        if (selectedRequest?.tripDriverLocation?.latitude && selectedRequest?.tripDriverLocation?.longitude) {
            return [
                {
                    _id: selectedAssignedDriverId,
                    driverName: selectedRequest.tripDriverName || 'السائق المكلف',
                    phoneNumber: selectedRequest.tripDriverPhoneNumber || '',
                    vechicleType: '',
                    state: 'active',
                    lastLocation: selectedRequest.tripDriverLocation,
                },
            ];
        }

        return [];
    }, [drivers, shouldIsolateAssignedDriver, selectedAssignedDriverId, selectedRequest]);

    const filteredMapDrivers = useMemo(() => {
        const term = normalizePhone(mapPhoneFilter);
        if (!term) return mapDrivers;
        return mapDrivers.filter((driver) => normalizePhone(driver?.phoneNumber).includes(term));
    }, [mapDrivers, mapPhoneFilter]);

    const selectedDriver = useMemo(
        () => filteredMapDrivers.find((d) => String(d._id) === String(selectedDriverId)) || null,
        [filteredMapDrivers, selectedDriverId]
    );

    const formatDate = (value) => {
        if (!value) return 'غير معروف';
        const date = new Date(value);
        return date.toLocaleString('ar-LY');
    };

    const formatStatus = (status, tripState = null) => {
        const normalizedTripState = String(tripState || '').toLowerCase();
        if (status === 'accepted') {
            if (normalizedTripState === 'trip_started') return 'الرحلة جارية';
            if (normalizedTripState === 'completed') return 'مكتملة';
            if (normalizedTripState === 'cancelled' || normalizedTripState === 'faild') return 'ملغية';
            if (normalizedTripState === 'driver_on_the_way') return 'السائق في الطريق';
            return 'مقبول';
        }
        switch (status) {
            case 'open':
                return 'قيد الانتظار';
            case 'accepted':
                return 'مقبول';
            default:
                return status || '';
        }
    };

    const formatProblemType = (value) => {
        if (!value) return 'غير محدد';
        const normalized = String(value).toLowerCase();
        if (normalized === 'sa7eba' || normalized === 'ساحبة') return 'ساحبة';
        if (normalized === 'rafe3a' || normalized === 'رافعة') return 'رافعة';
        return value;
    };

    const formatOfferStatus = (status) => {
        switch (status) {
            case 'pending':
                return 'قيد الانتظار';
            case 'accepted':
                return 'مقبول';
            case 'rejected':
                return 'مرفوض';
            default:
                return status || '';
        }
    };

    const rangeRadius = selectedRequest?.startPoint?.latitude && selectedRequest?.startPoint?.longitude && maxSearchRangeKm
        ? maxSearchRangeKm * 1000
        : null;

    const handleRedirect = async () => {
        if (!selectedRequest || !redirectDriverId) return;
        setRedirecting(true);
        await redirectRequest(selectedRequest._id, redirectDriverId);
        setRedirecting(false);
    };

    const toggleNotifyDriver = (driverId) => {
        const normalizedId = String(driverId);
        setNotifySelection((prev) => {
            const exists = prev.includes(normalizedId);
            if (exists) return prev.filter((id) => id !== normalizedId);
            return [...prev, normalizedId];
        });
    };

    const handleSaveNotify = async () => {
        setSavingNotify(true);
        await updateNotifyList(notifySelection);
        setSavingNotify(false);
        setShowNotifyModal(false);
    };

    const handleRedirectSearch = async () => {
        const query = (redirectSearchInput || '').trim();
        if (!query) {
            setRedirectSearchResults([]);
            setRedirectSearchTerm('');
            return;
        }
        setRedirectSearchLoading(true);
        setRedirectSearchTerm(query);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/drivers/drivers/search?q=${encodeURIComponent(query)}`, {
                headers: {
                    Authorization: `Bearer ${user?.token}`
                }
            });
            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'تعذر البحث عن السائق');
            }
            setRedirectSearchResults(Array.isArray(json) ? json : []);
        } catch (error) {
            setRedirectSearchResults([]);
        } finally {
            setRedirectSearchLoading(false);
        }
    };

    return (
        <section className="container activity-page">
            <header className="activity-header">
                <div className="notify-card" onClick={() => setShowNotifyModal(true)}>
                    <div>
                        <p>السائقون دائم الإشعار</p>
                        <strong>{notifyDriverIds.length}</strong>
                    </div>
                    <button type="button">إدارة</button>
                </div>
                <div className="activity-actions">
                    <div className="filters selects">
                        <label className="map-phone-filter-label">
                            <input
                                className="map-phone-filter-input"
                                type="text"
                                value={mapPhoneFilter}
                                onChange={(e) => setMapPhoneFilter(e.target.value)}
                                placeholder="تصفية السائقين برقم الهاتف"
                                disabled={loading}
                            />
                        </label>
                        <label>
                            
                            <select
                                value={filter}
                                onChange={(e) => applyFilter(e.target.value)}
                                disabled={loading}
                            >
                                {vehicleFilters.map((option) => (
                                    <option key={option} value={option}>
                                        {option === 'all' ? 'كل السائقين' : option}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            
                            <select
                                value={driverStateFilter}
                                onChange={(e) => applyDriverStateFilter(e.target.value)}
                                disabled={loading}
                            >
                                {driverStateFilters.map((option) => (
                                    <option key={option} value={option}>
                                        {option === 'all' ? 'كل الحالات' : option === 'active' ? 'نشط' : 'غير نشط'}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <button className="refresh" onClick={refresh} disabled={loading}>
                        <FiRefreshCw />
                        <span>{loading ? 'جار التحديث...' : 'تحديث'}</span>
                    </button>
                </div>
            </header>

            <div className="activity-stats">
                <div className="stat-card">
                    <p>عدد السائقين على الخريطة</p>
                    <strong>{drivers.length}</strong>
                </div>
                <div className="stat-card">
                    <p>الطلبات النشطة بدون رحلة</p>
                    <strong>{requests.length}</strong>
                </div>
            </div>

            <div className="activity-body">
                <div className="activity-map">
                    <div className="map-frame">
                        <MapContainer
                            center={center}
                            zoom={7}
                            minZoom={3}
                            maxZoom={18}
                            maxBounds={[[18, 7], [37, 28]]}
                            maxBoundsViscosity={0.5}
                            scrollWheelZoom
                            whenCreated={(instance) => {
                                mapRef.current = instance;
                                setTimeout(() => instance.invalidateSize(), 120);
                            }}
                        >
                            <TileLayer
                                url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                                subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                                attribution="Map data © Google"
                            />
                            <Recenter center={center} />

                            {rangeRadius ? (
                                <Circle
                                    center={[selectedRequest.startPoint.latitude, selectedRequest.startPoint.longitude]}
                                    radius={rangeRadius}
                                    pathOptions={{ color: '#2563eb', weight: 1, fillColor: '#2563eb', fillOpacity: 0.08 }}
                                />
                            ) : null}

                            {selectedRequest?.endingPoint?.latitude && selectedRequest?.endingPoint?.longitude ? (
                                <Marker
                                    position={[selectedRequest.endingPoint.latitude, selectedRequest.endingPoint.longitude]}
                                    icon={destinationPinIcon}
                                >
                                    <Tooltip direction="top" offset={[0, -10]}>نقطة الوصول</Tooltip>
                                </Marker>
                            ) : null}

                        {filteredMapDrivers
                            .filter((driver) => driver?.lastLocation?.latitude && driver?.lastLocation?.longitude)
                            .map((driver) => {
                                const normalizePhone = (val) => (val ? String(val).replace(/\D/g, '') : '');
                                const phoneKey = normalizePhone(driver.phoneNumber);
                                const isOfferDriver =
                                    offerDrivers.ids.has(String(driver._id)) ||
                                    (phoneKey && offerDrivers.phones.has(phoneKey));
                                const isNotified = Boolean(selectedRequest) && notifiedDriverSet.has(String(driver._id));
        						const icon = isNotified ? driverNotifiedDotIcon : (isOfferDriver ? driverOfferDotIcon : driverDotIcon);

                                return (
                                    <Marker
                                        key={`driver-${driver._id}`}
                                        position={[driver.lastLocation.latitude, driver.lastLocation.longitude]}
                                        icon={icon}
                                        eventHandlers={{
                                            click: () => {
                                                setSelectedDriverId(driver._id);
                                                if (selectedRequestId) {
                                                    setRedirectDriverId(String(driver._id));
                                                } else {
                                                    selectRequest(null);
                                                }
                                            },
                                        }}
                                    >
                                        <Tooltip direction="top" offset={[0, -20]}>{driver.driverName}</Tooltip>
                                        <Popup>
                                            <div className="popup-content">
                                                <h4>السائق</h4>
                                                <p>
                                                    <span>الاسم:</span> {driver.driverName}
                                                </p>
                                                <p>
                                                    <span>رقم الهاتف:</span> {driver.phoneNumber}
                                                </p>
                                                <p>
                                                    <span>نوع المركبة:</span> {driver.vechicleType || 'غير محدد'}
                                                </p>
                                                <p>
                                                    <span>الحالة:</span> {driver.state}
                                                </p>
                                                <p>
                                                    <span>آخر تحديث:</span> {formatDate(driver?.lastLocation?.updatedAt)}
                                                </p>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}

                        {requests
                            .filter((req) => req?.startPoint?.latitude && req?.startPoint?.longitude)
                            .map((request) => (
                                <Marker
                                    key={`request-${request._id}`}
                                    position={[request.startPoint.latitude, request.startPoint.longitude]}
                                    icon={requestDotIcon}
                                    eventHandlers={{
                                        click: () => {
                                            selectRequest(request._id);
                                            setSelectedDriverId(null);
                                        },
                                    }}
                                >
                                    <Tooltip direction="top" offset={[0, -20]}>
                                        {request.userName || 'طلب'}
                                    </Tooltip>
                                    <Popup>
                                        <div className="popup-content">
                                            <h4>طلب سحب</h4>
                                            <p className="selected-indicator">
                                                {selectedRequestId === request._id ? 'يتم التحديث لحظياً' : 'اضغط للتحديث اللحظي' }
                                            </p>
                                            <p>
                                                <span>المستخدم:</span> {request.userName} ({request.userPhoneNumber})
                                            </p>
                                            <p>
                                                <span>الحالة:</span> {formatStatus(request.status, request.tripState)}
                                            </p>
                                            <p>
                                                <span>نوع المشكلة:</span> {formatProblemType(request.problemType)}
                                            </p>
                                            <p>
                                                <span>نوع السيارة:</span> {request.carType}
                                            </p>
                                            <p>
                                                <span>السعر المبدئي:</span> {request.userPrice || request.basePrice || 0} د.ل
                                            </p>
                                            <div className="offers-block">
                                                <div className="offers-header">
                                                    <span>العروض ({request.offers?.length || 0})</span>
                                                </div>
                                                {request.offers && request.offers.length > 0 ? (
                                                    request.offers.map((offer) => (
                                                        <div key={offer._id} className="offer-row">
                                                            <div>
                                                                <p className="offer-driver">{offer.driverName}</p>
                                                                <p className="offer-meta">
                                                                    {offer.driverPhoneNumber} | {offer.vechicleType || 'غير محدد'}
                                                                </p>
                                                            </div>
                                                            <div className="offer-price">{offer.price} د.ل</div>
                                                            <div className={`offer-status ${offer.status || ''}`}>
                                                                {formatOfferStatus(offer.status)}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="no-offers">لا توجد عروض حالياً</p>
                                                )}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </div>

                    <div className="activity-requests">
                        <div className="requests-header">
                            <h4>الطلبات النشطة</h4>
                            <span className="muted">{requests.length} طلب</span>
                        </div>
                        {requests.length > 0 ? (
                            <div className="requests-list">
                                {requests.map((request) => (
                                    <button
                                        key={request._id}
                                        className={`request-row ${selectedRequestId === request._id ? 'active' : ''}`}
                                        onClick={() => {
                                            selectRequest(request._id);
                                            setSelectedDriverId(null);
                                        }}
                                    >
                                        <div className="row-main">
                                            <span className="user-name">{request.userName || 'مستخدم'}</span>
                                            <span className="user-phone">{request.userPhoneNumber || ''}</span>
                                        </div>
                                        <div className="row-meta">
                                            <span className={`status-badge ${request.status || ''}`}>
                                                {formatStatus(request.status, request.tripState)}
                                            </span>
                                            <span className="offers-count">العروض: {request.offers?.length || 0}</span>
                                            <span className="price">{request.userPrice || request.basePrice || 0} د.ل</span>
                                        </div>
                                        <div className="row-extra">
                                            <span>{formatProblemType(request.problemType)}</span>
                                            <span>{request.carType || ''}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="placeholder">لا توجد طلبات حالياً</div>
                        )}
                    </div>
                </div>

                <div className="activity-detail">
                    {!selectedDriver && !selectedRequest && (
                        <div className="placeholder">اضغط على سائق أو طلب لعرض التفاصيل</div>
                    )}

                    {selectedDriver && (
                        <div className="detail-card">
                            <div className="detail-card__header">
                                <div>
                                    <h3>السائق</h3>
                                    <p className="muted">آخر تحديث: {formatDate(selectedDriver?.lastLocation?.updatedAt)}</p>
                                </div>
                                {selectedDriver.carImage ? (
                                    <img src={selectedDriver.carImage} alt="صورة المركبة" className="driver-image" />
                                ) : null}
                            </div>
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <span className="label">الاسم</span>
                                    <span className="value">{selectedDriver.driverName}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">الهاتف</span>
                                    <span className="value">{selectedDriver.phoneNumber}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">نوع المركبة</span>
                                    <span className="value">{selectedDriver.vechicleType || 'غير محدد'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">الحالة</span>
                                    <span className="value">{selectedDriver.state}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedRequest && (
                        <div className="detail-card">
                            <h3>طلب سحب</h3>
                            <p><strong>المستخدم:</strong> {selectedRequest.userName} ({selectedRequest.userPhoneNumber})</p>
                            <p><strong>الحالة:</strong> {formatStatus(selectedRequest.status, selectedRequest.tripState)}</p>
                            <p><strong>نوع المشكلة:</strong> {formatProblemType(selectedRequest.problemType)}</p>
                            <p><strong>نوع السيارة:</strong> {selectedRequest.carType}</p>
                            <p><strong>السعر المبدئي:</strong> {selectedRequest.userPrice || selectedRequest.basePrice || 0} د.ل</p>
                            {maxSearchRangeKm ? (
                                <p className="muted">نطاق الإشعار: {maxSearchRangeKm} كم من موقع الطلب</p>
                            ) : null}

                            <div className="redirect-block">
                                <div className="redirect-row">
                                    <input
                                        type="text"
                                        placeholder="أدخل رقم هاتف السائق"
                                        value={redirectSearchInput}
                                        onChange={(e) => setRedirectSearchInput(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRedirectSearch}
                                        disabled={redirectSearchLoading}
                                    >
                                        {redirectSearchLoading ? 'جار البحث...' : 'بحث'}
                                    </button>
                                </div>
                                {redirectSearchTerm ? (
                                    redirectSearchResults.length ? (
                                        <div className="redirect-results">
                                            {redirectSearchResults.map((driver) => (
                                                <button
                                                    key={driver._id}
                                                    className={`redirect-option ${redirectDriverId === String(driver._id) ? 'active' : ''}`}
                                                    onClick={() => setRedirectDriverId(String(driver._id))}
                                                >
                                                    <span className="name">{driver.driverName}</span>
                                                    <span className="phone">{driver.phoneNumber}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="muted">لا توجد نتائج مطابقة</p>
                                    )
                                ) : null}
                                <div className="redirect-row">
                                    <button
                                        type="button"
                                        onClick={handleRedirect}
                                        disabled={!redirectDriverId || redirecting}
                                    >
                                        {redirecting ? 'جار الإرسال...' : 'إرسال للسائق'}
                                    </button>
                                </div>
                                
                            </div>
                            <div className="offers-list">
                                <div className="offers-header">العروض ({selectedRequest.offers?.length || 0})</div>
                                {selectedRequest.offers && selectedRequest.offers.length > 0 ? (
                                    selectedRequest.offers.map((offer) => (
                                        <div key={offer._id} className="offer-row">
                                            <div className="offer-main">
                                                <div className="offer-driver">{offer.driverName}</div>
                                                <div className="offer-meta">{offer.driverPhoneNumber} | {offer.vechicleType || 'غير محدد'}</div>
                                            </div>
                                            <div className="offer-price">{offer.price} د.ل</div>
                                            <div className={`offer-status ${offer.status || ''}`}>{formatOfferStatus(offer.status)}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="placeholder">لا توجد عروض حالياً</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {showNotifyModal ? (
                    <div className="notify-modal">
                        <div className="notify-modal__backdrop" onClick={() => setShowNotifyModal(false)} />
                        <div className="notify-modal__content">
                            <div className="notify-modal__header">
                                <div>
                                    <h3>إدارة قائمة الإشعارات الدائمة</h3>
                                    <p className="muted">السائقون هنا سيصلكم إشعار بكل طلب جديد.</p>
                                </div>
                                <button type="button" className="close" onClick={() => setShowNotifyModal(false)}>×</button>
                            </div>
                            <div className="notify-modal__body">
                                <div className="notify-selected">
                                    <div className="notify-selected__header">
                                        <span>السائقون المضافون ({selectedNotifyDrivers.length})</span>
                                    </div>
                                    {selectedNotifyDrivers.length ? (
                                        <div className="notify-selected__list">
                                            {selectedNotifyDrivers.map((driver) => (
                                                <div key={driver._id} className="selected-pill">
                                                    <div className="info">
                                                        <span className="name">{driver.driverName}</span>
                                                        <span className="phone">{driver.phoneNumber}</span>
                                                    </div>
                                                    <button type="button" onClick={() => toggleNotifyDriver(driver._id)}>إزالة</button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="muted">لا يوجد سائقون مضافون حالياً.</p>
                                    )}
                                </div>

                                <div className="notify-search">
                                    <input
                                        type="text"
                                        placeholder="ابحث برقم الهاتف"
                                        value={notifySearchInput}
                                        onChange={(e) => setNotifySearchInput(e.target.value)}
                                    />
                                    <button type="button" onClick={handleNotifySearch}>بحث</button>
                                </div>

                                {notifySearchTerm && filteredDrivers.length ? (
                                    <div className="notify-list">
                                        {filteredDrivers.map((driver) => {
                                            const driverId = String(driver._id);
                                            const checked = notifySelection.includes(driverId);
                                            return (
                                                <label key={driver._id} className={`notify-item ${checked ? 'checked' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleNotifyDriver(driverId)}
                                                    />
                                                    <div className="notify-item__info">
                                                        <span className="name">{driver.driverName}</span>
                                                        <span className="phone">{driver.phoneNumber}</span>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                ) : notifySearchTerm && !filteredDrivers.length ? (
                                    <p className="muted">لا توجد نتائج لهذا الرقم.</p>
                                ) : (
                                    <p className="muted"></p>
                                )}
                            </div>
                            <div className="notify-modal__footer">
                                <button type="button" onClick={() => setShowNotifyModal(false)} className="ghost">إلغاء</button>
                                <button type="button" onClick={handleSaveNotify} disabled={savingNotify}>
                                    {savingNotify ? 'جار الحفظ...' : 'حفظ القائمة'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
};
